import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQueryWithRetry = async (args, api, extraOptions) => {
    const base = fetchBaseQuery({ baseUrl: process.env.REACT_APP_MINIAPP_SERVICE_URL });
    const maxRetries = 2;
    let attempt = 0;
    let lastError;
    while (attempt <= maxRetries) {
        const res = await base(args, api, extraOptions);
        if (!res.error) return res;
        const status = res.error?.status;
        // Retry on 429/502/503/504/network
        if ([429, 502, 503, 504].includes(Number(status)) || status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
            lastError = res.error;
            const ms = 300 * Math.pow(2, attempt); // 300ms, 600ms, 1200ms
            await new Promise(r => setTimeout(r, ms));
            attempt += 1;
            continue;
        }
        return res;
    }
    return { error: lastError };
};

export const miniappApi = createApi({
    reducerPath: 'miniappApi',
    baseQuery: baseQueryWithRetry,
    endpoints: (builder) => ({
        validateInitData: builder.mutation({
            query: ({ projectId, initData }) => ({
                url: '/v1/validate',
                method: 'POST',
                params: { project_id: projectId },
                body: { init_data: initData },
            }),
        }),
        getProjectForView: builder.query({
            query: (projectId) => ({
                url: `/v1/project/${projectId}`,
                method: 'GET',
            }),
        }),
        trackEvent: builder.mutation({
            query: ({ projectId, sessionId, eventType, elementId, properties }) => ({
                url: '/v1/analytics/events',
                method: 'POST',
                body: {
                    project_id: projectId,
                    session_id: sessionId,
                    event_type: eventType,
                    element_id: elementId,
                    properties: properties ?? null,
                },
            }),
        }),
        trackEventsBulk: builder.mutation({
            query: ({ events }) => ({
                url: '/v1/analytics/events/bulk',
                method: 'POST',
                body: { events },
            }),
        }),
        submitAnalytics: builder.mutation({
            query: ({ projectId, formId, sessionId, data, username }) => ({
                url: '/v1/analytics/submissions',
                method: 'POST',
                body: {
                    project_id: projectId,
                    form_id: formId,
                    session_id: sessionId,
                    data,
                    ...(username ? { username } : {}),
                },
            }),
        }),
        exportSubmissionsToChat: builder.mutation({
            query: ({ projectId, formId, dateFrom, dateTo, limit = 10000, chatId }) => {
                const params = { project_id: projectId, form_id: formId, limit };
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
                if (chatId) params.chat_id = chatId;
                return {
                    url: '/v1/analytics/submissions/export_to_chat',
                    method: 'POST',
                    params,
                };
            },
        }),
        listEvents: builder.query({
            query: ({ projectId, limit = 100, offset = 0 }) => ({
                url: '/v1/analytics/events',
                method: 'GET',
                params: { project_id: projectId, limit, offset },
            }),
        }),
        listSubmissions: builder.query({
            query: ({ projectId, formId, dateFrom, dateTo, limit = 100, offset = 0 }) => {
                const params = { project_id: projectId, limit, offset };
                if (formId) params.form_id = formId;
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
                return {
                    url: '/v1/analytics/submissions',
                    method: 'GET',
                    params,
                };
            },
        }),
        eventsTimeseries: builder.query({
            query: ({ projectId, dateFrom, dateTo, interval = 'hour', eventType, elementId }) => {
                const params = { project_id: projectId, interval };
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
                if (eventType !== undefined && eventType !== null && eventType !== '' ) params.event_type = eventType;
                if (elementId) params.element_id = elementId;
                return {
                    url: '/v1/analytics/events/timeseries',
                    method: 'GET',
                    params,
                };
            },
        }),
        eventsBreakdown: builder.query({
            query: ({ projectId, dateFrom, dateTo, by = 'event_type', limit = 20 }) => {
                const params = { project_id: projectId, by, limit };
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
                return {
                    url: '/v1/analytics/events/breakdown',
                    method: 'GET',
                    params,
                };
            },
        }),
        fetchContact: builder.mutation({
            query: ({ projectId, initData, consume = true }) => ({
                url: '/v1/contact',
                method: 'POST',
                params: { project_id: projectId, consume },
                body: { init_data: initData },
            }),
        }),
        createInvoice: builder.mutation({
            query: ({ projectId, productId, quantity = 1, userId, username, pay_via, pay_currency }) => ({
                url: `/v1/projects/${projectId}/create_invoice`,
                method: 'POST',
                body: {
                    product_id: productId,
                    quantity,
                    user_id: userId ?? null,
                    username: username ?? null,
                    ...(pay_via ? { pay_via } : {}),
                    ...(pay_currency ? { pay_currency } : {}),
                },
            }),
        }),
        listOrders: builder.query({
            query: ({ projectId, limit = 100, offset = 0, status } = {}) => {
                const params = { limit, offset };
                if (status) params.status = status;
                return {
                    url: `/v1/projects/${projectId}/orders`,
                    method: 'GET',
                    params,
                };
            },
        }),
        listProducts: builder.query({
            query: ({ projectId, limit = 100, offset = 0 }) => ({
                url: `/v1/projects/${projectId}/products`,
                method: 'GET',
                params: { limit, offset },
            }),
        }),
        getProductPublic: builder.query({
            query: ({ projectId, productId }) => ({
                url: `/v1/projects/${projectId}/products/${productId}`,
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useValidateInitDataMutation,
    useGetProjectForViewQuery,
    useTrackEventMutation,
    useTrackEventsBulkMutation,
    useSubmitAnalyticsMutation,
    useExportSubmissionsToChatMutation,
    useListEventsQuery,
    useListSubmissionsQuery,
    useEventsTimeseriesQuery,
    useEventsBreakdownQuery,
    useFetchContactMutation,
    useCreateInvoiceMutation,
    useListOrdersQuery,
    useListProductsQuery,
    useGetProductPublicQuery,
} = miniappApi;
